import { __rest, __assign } from 'tslib';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { classNames } from '@panwds/react-ui';
import { useTableInstance } from '../TableContext/TableContext.esm.js';
import '../TableContext/TableProvider.esm.js';
import { useTableLayoutEnabled } from '../TableLayout/TableLayout.esm.js';
import '@panwds/icons';
import 'uuid';
import { Cell, CellWrapper } from './Cell.esm.js';
import { DragHandle } from './DragHandle.esm.js';

function groupCells(cells) {
    return cells.reduce(function (accum, cell) {
        if (cell.column.isSortRowColumn) {
            accum.sortRowCells.push(cell);
        }
        else if (cell.column.isSelectRowColumn) {
            accum.selectRowCells.push(cell);
        }
        else if (cell.isGrouped || cell.isPlaceholder) {
            accum.groupedCells.push(cell);
        }
        else if (cell.column.sticky) {
            if (cell.column.stickyPosition === "left") {
                accum.leftStickyCells.push(cell);
            }
            else {
                accum.rightStickyCells.push(cell);
            }
        }
        else {
            accum.generalCells.push(cell);
        }
        return accum;
    }, {
        sortRowCells: [],
        selectRowCells: [],
        groupedCells: [],
        generalCells: [],
        leftStickyCells: [],
        rightStickyCells: []
    });
}
function Row(_a) {
    var _b = _a.appearance, appearance = _b === void 0 ? "primary" : _b, _c = _a.rowSize, rowSize = _c === void 0 ? "standard" : _c, shouldHighlightRow = _a.shouldHighlightRow, shouldDisableRow = _a.shouldDisableRow, row = _a.row, isDragging = _a.isDragging, isHeld = _a.isHeld, sortRef = _a.sortRef, dragHandleProps = _a.dragHandleProps, style = _a.style, rest = __rest(_a, ["appearance", "rowSize", "shouldHighlightRow", "shouldDisableRow", "row", "isDragging", "isHeld", "sortRef", "dragHandleProps", "style"]);
    var intl = useIntl();
    var _d = groupCells(row.cells), sortRowCells = _d.sortRowCells, selectRowCells = _d.selectRowCells, groupedCells = _d.groupedCells, leftStickyCells = _d.leftStickyCells, generalCells = _d.generalCells, rightStickyCells = _d.rightStickyCells;
    var _e = useTableInstance().state.globalFilter, globalFilter = _e === void 0 ? "" : _e;
    var tertiaryClassName;
    if (appearance === "tertiary") {
        tertiaryClassName = classNames(row.subRows.length > 0
            ? "dark:tw-bg-gray-1000 tw-bg-gray-50 tw-border-solid tw-border-gray-300 tw-border-0 tw-border-b dark:tw-border-steel-blue-800"
            : "dark:tw-bg-gray-900 tw-bg-white tw-border-solid tw-border-gray-300 tw-border-0 tw-border-b dark:tw-border-steel-blue-700 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-steel-800");
    }
    var rowHeight = {
        standard: "tw-h-8",
        cozy: "tw-h-11",
        comfy: "tw-h-24"
    };
    var rowHeightClass = appearance !== "secondary" && rowHeight[rowSize];
    var className = classNames("tw-group tw-text-default", rowHeightClass, row.isSelected &&
        "tw-bg-blue-100 even:tw-bg-blue-100 dark:tw-bg-blue-steel-900 dark:even:tw-bg-blue-steel-900", appearance === "primary" &&
        !(shouldHighlightRow === null || shouldHighlightRow === void 0 ? void 0 : shouldHighlightRow({ row: row })) &&
        "tw-bg-white even:tw-bg-gray-50 dark:tw-bg-blue-steel-1000 dark:even:tw-bg-blue-steel-950 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-steel-800", appearance === "secondary" &&
        "tw-box-border tw-bg-white tw-border-solid tw-border-gray-300 tw-border-0 tw-border-b last:tw-border-b-0 dark:tw-bg-blue-steel-1000 dark:tw-border-blue-steel-800 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-steel-800", appearance === "tertiary" && tertiaryClassName, (shouldHighlightRow === null || shouldHighlightRow === void 0 ? void 0 : shouldHighlightRow({ row: row })) &&
        "tw-bg-blue-200 dark:tw-bg-blue-900 hover:tw-bg-blue-200 dark:hover:tw-bg-blue-steel-800", isDragging && "tw-opacity-50", isHeld && "tw-shadow-lg", !(shouldDisableRow === null || shouldDisableRow === void 0 ? void 0 : shouldDisableRow({ row: row })) && "dark:tw-text-blue-steel-300", (shouldDisableRow === null || shouldDisableRow === void 0 ? void 0 : shouldDisableRow({ row: row })) &&
        "tw-text-gray-400 dark:tw-text-blue-steel-600 tw-cursor-not-allowed tw-pointer-events-none");
    var tableLayoutEnabled = useTableLayoutEnabled();
    var _f = row.getRowProps(), styleProps = _f.style, rowProps = __rest(_f, ["style"]);
    var combinedStyles = __assign(__assign({ minHeight: "2rem" }, style), styleProps);
    if (tableLayoutEnabled) {
        combinedStyles.minWidth = combinedStyles.width;
        combinedStyles.width = undefined;
    }
    return (React.createElement("div", __assign({ id: "row-".concat(row.index), "aria-rowindex": row.index }, rowProps, { ref: sortRef, style: combinedStyles, className: className }, rest),
        !!selectRowCells.length && (React.createElement("div", { className: "tw-flex" }, selectRowCells.map(function (cell) { return (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column })); }))),
        !!groupedCells.length && (React.createElement("div", { className: "tw-flex" }, groupedCells.map(function (cell) { return (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column })); }))),
        !!leftStickyCells.length && (React.createElement("div", { style: {
                background: "inherit",
                position: "sticky",
                left: 0,
                zIndex: 1
            }, className: "tw-flex tw-shadow-left dark:tw-shadow-dark-left" }, leftStickyCells.map(function (cell) { return (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column })); }))),
        React.createElement("div", { className: "tw-flex tw-flex-grow" }, generalCells.map(function (cell, index) { return (React.createElement(Cell, { cell: cell, column: cell.column, key: cell.column.id, row: row, last: generalCells.length === index + 1 })); })),
        !!rightStickyCells.length && (React.createElement("div", { style: {
                background: "inherit",
                position: "sticky",
                right: 0
            }, className: "tw-flex tw-shadow-right dark:tw-shadow-dark-right" }, rightStickyCells.map(function (cell) { return (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column })); }))),
        !!sortRowCells.length && (React.createElement("div", { className: "tw-flex" }, sortRowCells.map(function (cell) { return (React.createElement(CellWrapper, { cell: cell, key: cell.column.id },
            React.createElement(DragHandle, __assign({}, dragHandleProps, { label: intl.formatMessage({ id: "o+pYwd", defaultMessage: [{ type: 0, value: "Move Row" }] }), isHeld: isHeld, disabled: globalFilter &&
                    intl.formatMessage({ id: "GOKfIG", defaultMessage: [{ type: 0, value: "Unable to sort while filtering" }] }) })))); })))));
}
function SortableRow(props) {
    var _a = useTableInstance().state.globalFilter, globalFilter = _a === void 0 ? "" : _a;
    var _b = useSortable({
        id: String(props.row.id),
        disabled: !!globalFilter
    }), isDragging = _b.isDragging, attributes = _b.attributes, listeners = _b.listeners, setNodeRef = _b.setNodeRef, transform = _b.transform, transition = _b.transition;
    var style = {
        transform: CSS.Transform.toString(transform),
        transition: transition
    };
    return (React.createElement(Row, __assign({ style: style }, props, { sortRef: setNodeRef, isDragging: isDragging, dragHandleProps: __assign(__assign({}, attributes), listeners) })));
}

export { Row, SortableRow };
//# sourceMappingURL=Row.esm.js.map
